import React, { useState } from 'react';
import { format } from 'date-fns';
import {
  Alert,
  Box,
  Button,
  Chip,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  CampaignPost,
  SocialCampaign,
  deliverCampaignPost,
  scheduleCampaignPost,
} from '../../api/socialStudio';
import { api } from '../../api/apiService';

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    color: '#f4f4f5',
    '& fieldset': { borderColor: '#3f3f46' },
    '&:hover fieldset': { borderColor: '#71717a' },
    '&.Mui-focused fieldset': { borderColor: '#818cf8' },
  },
  '& .MuiInputLabel-root': { color: '#a1a1aa' },
};

function apiMessage(err: unknown): string {
  const data = (err as { response?: { data?: unknown } })?.response?.data;
  if (typeof data === 'string' && data.trim()) return data;
  if (data && typeof data === 'object' && 'error' in data && typeof (data as { error: unknown }).error === 'string') {
    return (data as { error: string }).error;
  }
  if (err instanceof Error && err.message) return err.message;
  return 'Request failed';
}

function needsMedia(channel: string): boolean {
  return channel === 'instagram' || channel === 'threads' || channel === 'tiktok';
}

function statusLabel(post: CampaignPost): string {
  if (post.status === 'published') return 'Published';
  if (post.status === 'scheduled') return 'Scheduled';
  return 'Draft';
}

function statusColor(post: CampaignPost): { bg: string; color: string } {
  if (post.status === 'published') return { bg: '#14532d', color: '#bbf7d0' };
  if (post.status === 'scheduled') return { bg: '#1e3a8a', color: '#bfdbfe' };
  return { bg: '#27272a', color: '#d4d4d8' };
}

function toLocalInput(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function toRfc3339(local: string): string {
  return new Date(local).toISOString();
}

export function CampaignPostCalendar({
  campaign,
  onUpdated,
}: {
  campaign: SocialCampaign;
  onUpdated: (next: SocialCampaign) => void;
}) {
  const posts = campaign.posts || [];
  const [when, setWhen] = useState<Record<string, string>>({});
  const [recipient, setRecipient] = useState<Record<string, string>>({});
  const [editing, setEditing] = useState<Record<string, boolean>>({});
  const [copy, setCopy] = useState<Record<string, { body: string; hashtags: string }>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loginChannel, setLoginChannel] = useState('facebook');
  const [accountId, setAccountId] = useState('');
  const [token, setToken] = useState('');
  const [loginNote, setLoginNote] = useState<string | null>(null);

  const run = async (key: string, fn: () => Promise<SocialCampaign>) => {
    setBusy(key);
    setError(null);
    try {
      onUpdated(await fn());
    } catch (err) {
      setError(apiMessage(err));
    } finally {
      setBusy(null);
    }
  };

  return (
    <Box sx={{ px: 2, pt: 2 }}>
      <Typography sx={{ color: '#fafafa', fontWeight: 600 }}>Calendar</Typography>
      <Typography variant="body2" sx={{ color: '#a1a1aa', mt: 0.5, mb: 2, maxWidth: 640 }}>
        Schedule a time or post now. A scheduled post stays as it is until you edit it — then reschedule that copy or post it now. X, Facebook, LinkedIn, and SMS use the saved network login.
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 2, bgcolor: '#18181b', color: '#fecaca' }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {posts.length === 0 ? (
        <Typography variant="body2" sx={{ color: '#71717a', mb: 2 }}>
          No posts on this campaign yet. Add one from the cluster copy below.
        </Typography>
      ) : (
        <Stack spacing={1.5} sx={{ mb: 3 }}>
          {posts.map((post) => {
            const channel = (post.channel || '').toLowerCase();
            const media = needsMedia(channel);
            const colors = statusColor(post);
            const localWhen = when[post.id] ?? toLocalInput(post.scheduledAt);
            const smsTo = recipient[post.id] ?? post.recipient ?? '';
            const scheduled = post.status === 'scheduled';
            const openEditor = scheduled ? Boolean(editing[post.id]) : post.status !== 'published';
            const draft = copy[post.id] ?? { body: post.body, hashtags: post.hashtags ?? '' };
            const copyPayload = { body: draft.body, hashtags: draft.hashtags, updateCopy: true as const, recipient: smsTo };
            return (
              <Box key={post.id} sx={{ p: 2, borderRadius: 2, bgcolor: '#18181b', border: '1px solid #27272a' }}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Typography sx={{ color: '#fafafa', fontWeight: 600 }}>{post.platform || channel}</Typography>
                  <Chip size="small" label={statusLabel(post)} sx={{ bgcolor: colors.bg, color: colors.color, height: 22 }} />
                  {post.scheduledAt && post.status !== 'draft' ? (
                    <Typography variant="caption" sx={{ color: '#a1a1aa' }}>
                      {format(new Date(post.scheduledAt), 'MMM d, yyyy · p')}
                    </Typography>
                  ) : null}
                </Stack>
                {!openEditor && (
                  <>
                    <Typography variant="body2" sx={{ color: '#d4d4d8', mt: 1, whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                      {post.body}
                    </Typography>
                    {post.hashtags ? (
                      <Typography variant="caption" sx={{ color: '#93c5fd', display: 'block', mt: 0.75 }}>
                        {post.hashtags}
                      </Typography>
                    ) : null}
                  </>
                )}
                {post.externalId ? (
                  <Typography variant="caption" sx={{ color: '#71717a', display: 'block', mt: 0.75 }}>
                    Network id {post.externalId}
                  </Typography>
                ) : null}
                {post.deliveryError ? (
                  <Typography variant="caption" sx={{ color: '#fca5a5', display: 'block', mt: 0.75 }}>
                    {post.deliveryError}
                  </Typography>
                ) : null}
                {scheduled && !openEditor && !media ? (
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={busy !== null}
                      onClick={() => {
                        setCopy((prev) => ({
                          ...prev,
                          [post.id]: { body: post.body, hashtags: post.hashtags ?? '' },
                        }));
                        setEditing((prev) => ({ ...prev, [post.id]: true }));
                      }}
                      sx={{ color: '#fafafa', borderColor: '#3f3f46', textTransform: 'none' }}
                    >
                      Edit
                    </Button>
                  </Box>
                ) : null}
                {media ? (
                  <Typography variant="body2" sx={{ color: '#a1a1aa', mt: 1.5 }}>
                    {post.platform} needs a media upload before a post can go out.
                  </Typography>
                ) : openEditor ? (
                  <Stack spacing={1.25} sx={{ mt: 1.5 }}>
                    <TextField
                      size="small"
                      label="Post"
                      value={draft.body}
                      onChange={(e) => {
                        const value = e.target.value;
                        setCopy((prev) => {
                          const current = prev[post.id] ?? { body: post.body, hashtags: post.hashtags ?? '' };
                          return { ...prev, [post.id]: { ...current, body: value } };
                        });
                      }}
                      multiline
                      minRows={2}
                      sx={fieldSx}
                    />
                    <TextField
                      size="small"
                      label="Hashtags"
                      value={draft.hashtags}
                      onChange={(e) => {
                        const value = e.target.value;
                        setCopy((prev) => {
                          const current = prev[post.id] ?? { body: post.body, hashtags: post.hashtags ?? '' };
                          return { ...prev, [post.id]: { ...current, hashtags: value } };
                        });
                      }}
                      sx={fieldSx}
                    />
                    {channel === 'sms' && (
                      <TextField
                        size="small"
                        label="SMS recipient"
                        value={smsTo}
                        onChange={(e) => setRecipient((prev) => ({ ...prev, [post.id]: e.target.value }))}
                        sx={fieldSx}
                      />
                    )}
                    <TextField
                      size="small"
                      type="datetime-local"
                      label="Schedule"
                      value={localWhen}
                      onChange={(e) => setWhen((prev) => ({ ...prev, [post.id]: e.target.value }))}
                      InputLabelProps={{ shrink: true }}
                      sx={fieldSx}
                    />
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={!localWhen || !draft.body.trim() || busy !== null}
                        onClick={() =>
                          void run(post.id + ':schedule', async () => {
                            const next = await scheduleCampaignPost(campaign.id, post.id, {
                              scheduledAt: toRfc3339(localWhen),
                              ...copyPayload,
                            });
                            setEditing((prev) => ({ ...prev, [post.id]: false }));
                            return next;
                          })
                        }
                        sx={{ color: '#fafafa', borderColor: '#3f3f46', textTransform: 'none' }}
                      >
                        {busy === post.id + ':schedule' ? 'Scheduling…' : scheduled ? 'Reschedule' : 'Schedule'}
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        disabled={!draft.body.trim() || busy !== null}
                        onClick={() =>
                          void run(post.id + ':send', () =>
                            deliverCampaignPost(campaign.id, post.id, copyPayload)
                          )
                        }
                        sx={{ bgcolor: '#6366f1', textTransform: 'none', '&:hover': { bgcolor: '#4f46e5' } }}
                      >
                        {busy === post.id + ':send' ? 'Posting…' : 'Post now'}
                      </Button>
                      {scheduled && (
                        <Button
                          size="small"
                          disabled={busy !== null}
                          onClick={() => setEditing((prev) => ({ ...prev, [post.id]: false }))}
                          sx={{ color: '#a1a1aa', textTransform: 'none' }}
                        >
                          Cancel
                        </Button>
                      )}
                    </Stack>
                  </Stack>
                ) : null}
              </Box>
            );
          })}
        </Stack>
      )}

      <Typography sx={{ color: '#fafafa', fontWeight: 600, mt: 1 }}>Network login</Typography>
      <Typography variant="body2" sx={{ color: '#a1a1aa', mt: 0.5, mb: 1.5 }}>
        Save the token for the channel you want to send. SMS account id is SID|from-number.
      </Typography>
      {loginNote && (
        <Alert severity="success" sx={{ mb: 1.5, bgcolor: '#18181b', color: '#bbf7d0' }} onClose={() => setLoginNote(null)}>
          {loginNote}
        </Alert>
      )}
      <Stack spacing={1.25} sx={{ maxWidth: 420, mb: 3 }}>
        <TextField
          select
          size="small"
          label="Channel"
          value={loginChannel}
          onChange={(e) => setLoginChannel(e.target.value)}
          SelectProps={{ native: true }}
          sx={fieldSx}
        >
          <option value="facebook">Facebook</option>
          <option value="x">X</option>
          <option value="linkedin">LinkedIn</option>
          <option value="sms">SMS</option>
        </TextField>
        <TextField size="small" label="Account id" value={accountId} onChange={(e) => setAccountId(e.target.value)} sx={fieldSx} />
        <TextField
          size="small"
          type="password"
          label="Token"
          value={token}
          onChange={(e) => setToken(e.target.value)}
          sx={fieldSx}
        />
        <Button
          variant="outlined"
          disabled={!token.trim() || busy !== null}
          onClick={() =>
            void (async () => {
              setBusy('login');
              setError(null);
              try {
                await api.put('/api/desk/social-accounts', {
                  channel: loginChannel,
                  account_id: accountId.trim(),
                  token: token,
                });
                setToken('');
                setLoginNote(`Saved ${loginChannel} login`);
              } catch (err) {
                setError(apiMessage(err));
              } finally {
                setBusy(null);
              }
            })()
          }
          sx={{ alignSelf: 'flex-start', color: '#fafafa', borderColor: '#3f3f46', textTransform: 'none' }}
        >
          Save login
        </Button>
      </Stack>
    </Box>
  );
}
